import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import React, { useState, useEffect } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useWatch } from "react-hook-form";
import { Question } from "../../components/question";
import { Section } from "../../components/section";
import { useQuestions } from "../../hooks/useQuestions";
import { availableQuestionTypes } from "../../support/availableQuestionTypes";
import { RegistrationFormSectionProps } from "../../types/registrationFormTypes";

export const RegistrationFormSection = ({
  sectionId,
  sectionIndex,
  control,
  register,
  getValues,
  errors,
  onDelete,
  onDuplicate,
}: RegistrationFormSectionProps) => {
  
  const { fields, add, remove, duplicate, move } = useQuestions(
    control,
    sectionIndex,
    getValues
  );

  const [showDescription, setShowDescription] = useState<Record<number, boolean>>({});
  const toggleDescription = (index: number, show: boolean) => {
    setShowDescription(prev => ({ ...prev, [index]: show }));
  };

  const questions = useWatch({
    control,
    name: `sections.${sectionIndex}.questions`,
  });

  useEffect(() => {
    if (questions && Array.isArray(questions)) {
      const initialShowDescription: Record<number, boolean> = {};
      questions.forEach((question: any, index: number) => {
        if (question?.description && question.description.trim() !== "") {
          initialShowDescription[index] = true;
        }
      });
      if (Object.keys(initialShowDescription).length > 0) {
        setShowDescription(prev => ({ ...prev, ...initialShowDescription }));
      }
    }
  }, [questions]);

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    move(result.source.index, result.destination.index);
  }

  return (
    <Section
      isOpen
      hasBorder
      hasMarginTop
      showActions
      label={`Seção ${sectionIndex + 1}`}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
    >
      <Row>
        <Col>
          <Form.Group>
            <Form.Label required>Nome da seção</Form.Label>

            <Form.Control
              {...register(`sections.${sectionIndex}.name`)}
              isInvalid={!!errors.sections?.[sectionIndex]?.name}
            />

            <Form.Control.Feedback type="invalid" className="d-block">
              {errors.sections?.[sectionIndex]?.name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={String(sectionId)}>
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="w-full">
              {fields.map((field: any, index: number) => (
                <Draggable
                  key={field.id}
                  draggableId={String(field.id)}
                  index={index}
                >
                  {(drag) => (
                    <div
                      ref={drag.innerRef}
                      {...drag.draggableProps}
                      {...drag.dragHandleProps}
                      className="mb-2"
                    >
                      <Question
                        key={field.id}
                        questionField={field}
                        sectionIndex={sectionIndex}
                        questionIndex={index}
                        register={register}
                        control={control}
                        errors={errors}
                        availableQuestionTypes={availableQuestionTypes}
                        showDescription={!!showDescription[index]}
                        toggleDescription={toggleDescription}
                        removeQuestion={() => remove(index)}
                        duplicateQuestion={() => duplicate(index)}
                      />
                    </div>
                  )}
                </Draggable>
              ))}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Row>
        <Col>
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              add();
            }}
            className="d-flex align-items-center gap-2 bg-transparent text-primary border border-dashed border-primary"
          >
            <FontAwesomeIcon icon={["fas", "plus"]} />
            Adicionar pergunta
          </Button>
        </Col>
      </Row>
    </Section>
  );
};
